import { Box, Button, Tab, Tabs, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import DropDown from '../baseComponents/dropdown';
import apiClient from '../../axiosClient';
import { InteractionAudit } from '../../interfaces/interactionAudit';
import { interactionAuditDispositionOptions } from '../../constants/interactionAuditConstants';
import { commentTextPattern } from '../../constants/patterns';
import { decodedJWT, getOwnerType } from '../../utils/commonUtils';
import JWTTokenPayload from '../../interfaces/JWTTokenPayload';
import RoleManagement from '../../security/roleManagement';

interface DispositionsSelectorProps {
    selectedDispositions: Record<number, string>;
    setSelectedDispositions: React.Dispatch<React.SetStateAction<Record<number, string>>>;
    handleSelectDisposition: (value: string, level: number) => void;
    row: InteractionAudit;
    onNext: () => void;
}

const DispositionsSelector: React.FC<DispositionsSelectorProps> = ({ 
    selectedDispositions,  
    setSelectedDispositions,
    row, 
    onNext 
}) => {
    const [comment, setComment] = useState('');
    const [isCompleted, setIsCompleted] = useState(false);

    const token: JWTTokenPayload|false = decodedJWT();
    const owner_type = getOwnerType(token);

    useEffect(() => {
        if (row.status === 'Completed') {
          setIsCompleted(true);
          let commentText: string = '';
          let dispositionOrder: string = '';
      
          if (RoleManagement.isClient(owner_type)) {
            commentText = row.client_audit_comment ?? '';
            dispositionOrder = row.client_audit_value ?? '';
          } 
          else if (RoleManagement.isAgency(owner_type)) {
            commentText = row.agency_audit_comment ?? '';
            dispositionOrder = row.agency_audit_value ?? '';
          }
          setComment(commentText);
      
          if (dispositionOrder) {
            const dispositionsArray = dispositionOrder.split('-');
            const dispositionsRecord: Record<number, string> = {};
            dispositionsArray.forEach((disp, idx) => {
              dispositionsRecord[idx] = disp;
            });
            setSelectedDispositions(dispositionsRecord);
          }
        } else {
          setIsCompleted(false);
          setComment('');
        }
    }, [row]);
    

    const handleSelectDisposition = (value: string, level: number) => {
        setSelectedDispositions((prev) => {
            const updatedDispositions: Record<number, string> = {
                ...prev,
                [level]: value
            };
            if (level === 0) {
                updatedDispositions[1] = "";
            }
            return updatedDispositions;
        });
    };

    const handleSubmit = async () => {
        if (!comment.trim()) {
            alert("Comment is required.");
            return;
        }

        const requestBody = {
            disposition_order: Object.values(selectedDispositions).join("-"), // Convert object values to a hyphen-separated string
            comment
        };

        try {
            const response = await apiClient.put(`/api/v1/interaction_audit/${row.interaction_audit_id}`, requestBody);

            if (response.status === 200) {
                alert("Successfully submitted interaction audit response.");
                setSelectedDispositions({});
                setComment('');
                row.status = "Completed";
                if (RoleManagement.isClient(owner_type)) {
                    row.client_audit_comment = comment;
                    row.client_audit_value = Object.values(selectedDispositions).join("-");
                  } 
                  else if (RoleManagement.isAgency(owner_type)) {
                    row.agency_audit_comment = comment;
                    row.agency_audit_value = Object.values(selectedDispositions).join("-");
                }
                onNext();
            } else {
                alert(`Unexpected response status: ${response.status}`);
            }
        } catch (error: any) {
            alert(error.response?.data?.message || error.message || "An error occurred while submitting.");
        }
    };

    return (
        <div style={{ width: 'auto', padding: "4px" }}>

            <Tabs value={0} sx={{ marginTop: "4px" }}>
                <Tab label="Add Disposition" style={{ minWidth: "100%", fontWeight: "bolder", fontSize: "18px" }} />
            </Tabs>
            <Box sx={{ display: "flex", flexDirection: "column", }}>
                <Typography variant="h6">Disposition Selector</Typography>

                {/* Level 0 Disposition */}
                <DropDown
                    label="Level 0 Disposition"
                    keyProp="disposition_id"
                    valueProp="disposition_name"
                    displayProp="disposition_label"
                    selectedValue={selectedDispositions[0] || ""}
                    onSelection={(value) => handleSelectDisposition(value, 0)}
                    items={Object.keys(interactionAuditDispositionOptions).map((key, index) => ({
                        disposition_id: index,
                        disposition_name: key,
                        disposition_label: key
                    }))}
                    disabled={isCompleted}
                />

                {/* Level 1 Disposition (Only appears when Level 0 is selected) */}
                {selectedDispositions[0] && (
                    <DropDown
                        label="Level 1 Disposition"
                        keyProp="disposition_id"
                        valueProp="disposition_name"
                        displayProp="disposition_label"
                        selectedValue={selectedDispositions[1] || ""}
                        onSelection={(value) => handleSelectDisposition(value, 1)}
                        items={interactionAuditDispositionOptions[selectedDispositions[0]] || []}
                        disabled={isCompleted}
                    />
                )}
                {selectedDispositions[1] && (
                    <DropDown
                        label="Next Interaction Required?"
                        keyProp="value"
                        valueProp="value"
                        displayProp="label"
                        selectedValue={selectedDispositions[2] || ""}
                        onSelection={(value) => handleSelectDisposition(value, 2)}
                        items={[
                            { value: "yes", label: "Yes" },
                            { value: "no", label: "No" }
                        ]}
                        disabled={isCompleted}
                    />
                )}
            </Box>
            <Box>
                <TextField
                    margin='normal'
                    type="text"
                    label="Comment"
                    value={comment}
                    onChange={(event) => setComment(event?.target.value.replace(commentTextPattern, ""))}
                    fullWidth
                    multiline
                    rows={3}
                    disabled={isCompleted}
                    slotProps={{
                        htmlInput: {
                            maxLength: 1000,
                        },
                    }}
                />
            </Box>
            <Box mt={3} mb={3} textAlign="center" >
                <Button variant="contained" onClick={handleSubmit} disabled={!selectedDispositions[2] || isCompleted} color="primary" fullWidth>Submit</Button>
            </Box>
        </div >
    );
};

export default DispositionsSelector;
